import { usePageData } from './usePageData'
import { ordersHiddenForShop } from '../lib/orderSection'

/**
 * Whether this shop's Orders section is hidden, and whether we know yet.
 *
 * The hub lives in the shop's settings, so this is an answer that arrives
 * rather than one that is simply known — which is why `known` is returned
 * alongside it. The /orders route needs to wait for it: rendering the Orders
 * page and then redirecting would flash a section that is meant to be hidden,
 * and redirecting first would bounce a two-way seller off a page that is
 * genuinely theirs.
 *
 * usePageData rather than a fetch of its own, because it caches by URL at
 * module level — Sidebar, ShopifyNavMenu and Dashboard all read the same
 * settings URL, so on any navigation after the first this answers from cache
 * with no request and no wait.
 */
export function useOrdersHidden(shop) {
  const url = shop ? `/api/settings?shop=${encodeURIComponent(shop)}` : null
  const { data, loading } = usePageData(url, { skip: !shop })

  // No shop yet is not an answer about the hub, so nothing is decided on it.
  if (!shop) return { hidden: false, known: false }

  const hub = data?.sync?.syncSource
  // The custom-app shop keeps the section whatever its hub — see
  // lib/orderSection. Read from the same settings response as the hub, so
  // there is no second request and no window where one is known and the other
  // is not.
  const customApp = data?.shopify?.customApp === true
  // A failed or empty settings read leaves the hub unknown. Treated as "show",
  // matching lib/orderSection: a seller seeing a section they could have been
  // spared is a far smaller fault than one losing a section they rely on
  // because a request failed.
  return { hidden: ordersHiddenForShop(hub, customApp), known: !loading && !!hub }
}
